import { createClient } from '@supabase/supabase-js';

// Supabase configuration
// .trim() guards against a trailing newline/whitespace ending up in the
// Vercel-stored env var value (e.g. from `echo "$KEY" | vercel env add`,
// which appends a newline, or a copy-paste that grabs a trailing blank
// line). An untrimmed key gets passed straight into the Realtime
// WebSocket URL as an `apikey=` query param, where the newline is
// percent-encoded as a literal `%0A` and the server rejects the
// connection outright — REST calls still work since fetch/XHR headers
// tolerate it, so this only surfaces as broken Q&A/Announcement live
// updates, not a page-load failure.
const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || 'YOUR_SUPABASE_URL').trim();
const supabaseAnonKey = (process.env.REACT_APP_SUPABASE_ANON_KEY || 'YOUR_SUPABASE_ANON_KEY').trim();

// Check if Supabase is properly configured
const isSupabaseConfigured = supabaseUrl !== 'YOUR_SUPABASE_URL' &&
                            supabaseAnonKey !== 'YOUR_SUPABASE_ANON_KEY' &&
                            supabaseUrl &&
                            supabaseAnonKey &&
                            !supabaseUrl.includes('placeholder');

if (!isSupabaseConfigured) {
  console.warn('⚠️ Supabase not configured. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env file');
}

// Create Supabase client or mock client for development
let supabase;
try {
  if (isSupabaseConfigured) {
    // Validate URL format
    new URL(supabaseUrl); // This will throw if URL is invalid
    
    // Create Supabase client with real-time configuration
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      realtime: {
        params: {
          eventsPerSecond: 10
        }
      },
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false
      }
    });
    
    // Supabase client initialized with real-time configuration

  } else {
    // Create a mock client for development

    supabase = createMockSupabaseClient();
  }
} catch (error) {
  console.error('❌ Failed to initialize Supabase client:', error.message);

  supabase = createMockSupabaseClient();
}

// Mock Supabase client for development when not configured
function createMockSupabaseClient() {
  const mockResponse = { data: [], error: null };
  const mockSingleResponse = { data: null, error: null };

  return {
    from: () => ({
      select: () => ({
        eq: () => ({
          single: () => Promise.resolve(mockSingleResponse),
          order: () => Promise.resolve(mockResponse)
        }),
        order: () => Promise.resolve(mockResponse)
      }),
      insert: () => ({
        select: () => ({
          single: () => Promise.resolve(mockSingleResponse)
        })
      })
    }),
    auth: {
      signInWithPassword: () => Promise.resolve(mockSingleResponse),
      signOut: () => Promise.resolve({ error: null }),
      getUser: () => {

        return Promise.resolve({ data: { user: null }, error: null });
      },
      getSession: () => {

        return Promise.resolve({ data: { session: null }, error: null });
      },
      onAuthStateChange: () => {

        return { data: { subscription: { unsubscribe: () => {} } } };
      }
    },
    channel: () => ({
      on: () => ({
        subscribe: () => () => {} // Return unsubscribe function directly
      }),
      subscribe: () => () => {} // Return unsubscribe function directly
    }),
    removeChannel: () => {}
  };
}

// Session management utilities
export const sessionUtils = {
  async refreshSession() {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      return { data, error };
    } catch (error) {
      console.error('❌ Exception during session refresh:', error);
      return { data: null, error };
    }
  },

  async validateAndRefreshSession() {
    
    const { data: { session }, error } = await supabase.auth.getSession();
    
    
    if (error) {
      console.error('❌ Error getting session:', error);
      return { isValid: false, session: null, error };
    }
    
    if (!session) {
      console.warn('⚠️ No session found');
      return { isValid: false, session: null, error: { message: 'No session found' } };
    }
    
    // Check if session is expired or about to expire (within 5 minutes)
    const expiryTime = new Date(session.expires_at * 1000);
    const now = new Date();
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
    
    
    if (expiryTime <= fiveMinutesFromNow) {
      const refreshResult = await this.refreshSession();
      
      if (refreshResult.error || !refreshResult.data?.session) {
        console.error('❌ Failed to refresh session:', refreshResult.error);
        return { isValid: false, session: null, error: refreshResult.error || { message: 'Session refresh failed' } };
      }
      
      return { isValid: true, session: refreshResult.data.session, error: null };
    }
    
    return { isValid: true, session, error: null };
  }
};

export { supabase };

// Database helper functions
export const dbHelpers = {
  // Include session utilities
  sessionUtils,
  // Sessions
  async getSessions(conferenceYear = undefined) {
    let query = supabase
      .from('sessions')
      .select('*')
      .order('start_time', { ascending: true });

    if (conferenceYear) {
      query = query.eq('conference_year', conferenceYear);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Error fetching sessions:', error);
      return { data: [], error };
    }

    return { data, error: null };
  },

  async getSession(id) {
    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('❌ Error fetching session:', error);
    } else {
    }

    return { data, error };
  },

  async getSessionByNumber(sessionNumber, conferenceYear = undefined) {
    try {
      let query = supabase
        .from('sessions')
        .select('*')
        .eq('session_number', sessionNumber);

      if (conferenceYear) {
        query = query.eq('conference_year', conferenceYear);
      }

      const { data, error } = await query.single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          console.warn('⚠️ ROUTING FIX: Session not found by session_number:', sessionNumber);
          return { data: null, error: null };
        }
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('❌ ROUTING FIX: Error fetching session by session_number:', error);
      return { data: null, error };
    }
  },

  async getSessionByTrack(track, conferenceYear = undefined) {
    let query = supabase
      .from('sessions')
      .select('*')
      .eq('track', track);

    if (conferenceYear) {
      query = query.eq('conference_year', conferenceYear);
    }

    const { data, error } = await query.single();

    if (error) {
      console.error('❌ Error fetching session by track:', error);
    } else {
      // Found session by track
    }

    return { data, error };
  },

  // Announcements
  async getAnnouncements(conferenceYear = undefined) {
    let query = supabase
      .from('announcements')
      .select('*')
      .order('created_at', { ascending: false });

    if (conferenceYear) {
      query = query.eq('conference_year', conferenceYear);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Error fetching announcements:', error);
    } else {
      // Log each announcement individually for debugging
      data?.forEach((announcement, index) => {
      });
    }

    return { data, error };
  },

  // Sponsors
  async getSponsors() {
    const { data, error } = await supabase
      .from('sponsors')
      .select('*')
      .order('tier', { ascending: true });

    if (error) {
      console.error('❌ Error fetching sponsors:', error);
    } else {
    }

    return { data, error };
  },

  async getSponsor(id) {
    const { data, error } = await supabase
      .from('sponsors')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('❌ Error fetching sponsor:', error);
    } else {
    }

    return { data, error };
  },

  async getSponsorAdvert() {
    // Try to get from a settings table first, fallback to sponsors table
    const { data, error } = await supabase
      .from('settings')
      .select('value')
      .eq('key', 'sponsor_advert_image')
      .single();

    if (error) {
      console.error('❌ Error fetching sponsor advert:', error);
      return { data: { image_url: null }, error };
    }

    return { data: { image_url: data?.value }, error: null };
  },

  async updateSponsorAdvert(imageUrl) {
    try {
      // Use upsert with onConflict to handle both insert and update
      const { data, error } = await supabase
        .from('settings')
        .upsert({
          key: 'sponsor_advert_image',
          value: imageUrl,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error upserting sponsor advert:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('❌ Unexpected error in updateSponsorAdvert:', err);
      return { data: null, error: err };
    }
  },

  async getSponsorAdverts() {

    try {
      const { data, error } = await supabase
        .from('settings')
        .select('id, key, value')
        .like('key', 'sponsor_advert_%')
        .order('key', { ascending: true });

      if (error) {
        console.error('❌ Error fetching sponsor adverts:', error);
        return { data: [], error };
      }

      // Return structured data with IDs for proper deletion
      const adverts = data?.map(item => ({
        id: item.id,
        key: item.key,
        image_url: item.value,
        url: item.value // For backward compatibility
      })).filter(advert => advert.image_url) || [];

      return { data: adverts, error: null };
    } catch (err) {
      console.error('❌ Unexpected error in getSponsorAdverts:', err);
      return { data: [], error: err };
    }
  },

  async addSponsorAdvert(imageUrl) {

    try {
      // Use timestamp-based unique key to avoid conflicts
      const timestamp = Date.now();
      const uniqueKey = `sponsor_advert_${timestamp}`;

      const { data, error } = await supabase
        .from('settings')
        .insert({
          key: uniqueKey,
          value: imageUrl,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error adding sponsor advert:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('❌ Unexpected error in addSponsorAdvert:', err);
      return { data: null, error: err };
    }
  },

  async deleteSponsorAdvert(advertId) {

    try {
      const { data, error } = await supabase
        .from('settings')
        .delete()
        .eq('id', advertId)
        .like('key', 'sponsor_advert_%');

      if (error) {
        console.error('❌ Error deleting sponsor advert:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('❌ Unexpected error in deleteSponsorAdvert:', err);
      return { data: null, error: err };
    }
  },

  // Q&A
  async getQuestions(sessionId = null, conferenceYear = undefined) {
    // Fetching approved questions from database
    let query = supabase
      .from('questions')
      .select('*')
      .in('status', ['approved', 'answered']) // Only show approved or answered questions
      .order('created_at', { ascending: false });

    if (sessionId) {
      query = query.eq('session_id', sessionId);
    }

    if (conferenceYear) {
      query = query.eq('conference_year', conferenceYear);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Error fetching questions:', error);
    } else {
      // Successfully fetched approved questions
    }

    return { data, error };
  },

  async submitQuestion(question) {
    
    // Submitting question for admin approval
    // Add status as 'pending' for admin approval
    const questionWithStatus = {
      ...question,
      status: 'pending' // Questions start as pending until approved by admin
    };
    
    
    const { data, error } = await supabase
      .from('questions')
      .insert([questionWithStatus])
      .select()
      .single();


    if (error) {
      console.error('❌ Error submitting question:', error);
    } else {
    }

    return { data, error };
  },

  async voteForQuestion(questionId) {
    // Get user's IP for anonymous voting (in a real app, you'd get this from the server)
    const userIP = null; // Will be handled by the SQL function

    const { data, error } = await supabase
      .rpc('increment_question_votes', {
        question_id: questionId,
        voter_ip: userIP
      });

    if (error) {
      console.error('❌ Error voting for question:', error);
    } else {
    }

    return { data, error };
  },

  async answerQuestion(questionId, answer, answeredBy) {
    // Answering question
    const { data, error } = await supabase
      .from('questions')
      .update({
        response: answer, // Use 'response' instead of 'answer' to match schema
        responded_by: answeredBy, // Use 'responded_by' instead of 'answered_by'
        responded_at: new Date().toISOString(), // Use 'responded_at' instead of 'answered_at'
        answered: true,
        status: 'answered' // Update status to answered
      })
      .eq('id', questionId)
      .select()
      .single();

    if (error) {
      console.error('❌ Error answering question:', error);
    } else {
      // Question answered successfully
    }

    return { data, error };
  },

  // Admin functions for question moderation
  async approveQuestion(questionId) {
    // Approving question
    const { data, error } = await supabase
      .from('questions')
      .update({
        status: 'approved'
      })
      .eq('id', questionId)
      .select()
      .single();

    if (error) {
      console.error('❌ Error approving question:', error);
    } else {
      // Question approved successfully
    }

    return { data, error };
  },

  async rejectQuestion(questionId, reason = null) {
    // Rejecting question
    const { data, error } = await supabase
      .from('questions')
      .update({
        status: 'rejected',
        moderation_notes: reason
      })
      .eq('id', questionId)
      .select()
      .single();

    if (error) {
      console.error('❌ Error rejecting question:', error);
    } else {
      // Question rejected successfully
    }

    return { data, error };
  },

  async getPendingQuestions() {
    // Fetching pending questions for admin review
    const { data, error } = await supabase
      .from('questions')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching pending questions:', error);
    } else {
      // Successfully fetched pending questions
    }

    return { data, error };
  },

  // Admin authentication
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      console.error('❌ Sign in error:', error);
    } else {
    }

    return { data, error };
  },

  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('❌ DEBUG: Sign out error:', error);
        console.error('❌ DEBUG: Error details:', {
          message: error.message,
          status: error.status,
          statusText: error.statusText
        });
      } else {
      }

      return { error };
    } catch (exception) {
      console.error('❌ DEBUG: Exception during sign out:', exception);
      return { error: exception };
    }
  },

  async getCurrentUser() {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    // Additional session validation
    if (session && session.expires_at) {
      const expiryDate = new Date(session.expires_at * 1000);
      const now = new Date();
      if (expiryDate <= now) {
        console.warn('⚠️ Session has expired:', { expiryDate, now });
        return { user: null, error: { message: 'Session expired' } };
      }
    }
    
    return { user: session?.user || null, error };
  },

  // Check if current user is an admin
  async isCurrentUserAdmin() {
    
    try {
      // First check if we have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('❌ Session error in isCurrentUserAdmin:', sessionError);
        return { isAdmin: false, error: sessionError };
      }
      
      if (!session || !session.user) {
        console.warn('⚠️ No valid session found in isCurrentUserAdmin');
        return { isAdmin: false, error: { message: 'No valid session' } };
      }
      
      // Use the secure database function instead of accessing auth.users directly
      const { data, error } = await supabase.rpc('is_current_user_admin');
      

      if (error) {
        console.error('❌ Error in is_current_user_admin RPC:', error);
        return { isAdmin: false, error };
      }

      const isAdmin = !!data;

      return { isAdmin, error: null };
    } catch (error) {
      console.error('🔍 Debug: Exception in isCurrentUserAdmin:', error);
      return { isAdmin: false, error };
    }
  },

  // Real-time subscriptions
  subscribeToAnnouncements(callback) {
    const subscription = supabase
      .channel('announcements')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'announcements'
      }, callback)
      .subscribe();

    // Return unsubscribe function
    return () => {
      supabase.removeChannel(subscription);
    };
  },

  // Helper method to get user IP (for anonymous bookmarking)
  getUserIP() {
    // In a real application, you would get this from the server
    // For now, we'll use a fake IP address format since the database expects inet type
    // Generate a consistent fake IP based on session
    if (!window.sessionStorage.getItem('user_session_id')) {
      window.sessionStorage.setItem('user_session_id',
        'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now()
      );
    }

    const sessionId = window.sessionStorage.getItem('user_session_id');
    // Convert session ID to a fake IP address format (192.168.x.x)
    const hash = sessionId.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);

    const ip1 = Math.abs(hash) % 256;
    const ip2 = Math.abs(hash >> 8) % 256;
    const fakeIP = `192.168.${ip1}.${ip2}`;
    return fakeIP;
  },

  // Bookmarks
  async getUserBookmarks() {
    
    // Call the RPC function with client IP for anonymous users
    const { data, error } = await supabase.rpc('get_user_bookmarks', {
      client_ip: this.getUserIP()
    });


    if (error) {
      console.error('❌ Error fetching bookmarks:', error);
      console.error('🔍 Debug: Error details:', JSON.stringify(error, null, 2));
      return { data: [], error };
    }
    
    const sessionIds = data ? data.map(row => row.session_id) : [];
    
    return { data: sessionIds, error: null };
  },

  async addBookmark(sessionId) {
    const { data, error } = await supabase.rpc('add_bookmark', {
      session_id: sessionId,
      client_ip: this.getUserIP()
    });

    if (error) {
      console.error('❌ Error adding bookmark:', error);
      console.error('🔍 Debug: Error details:', JSON.stringify(error, null, 2));
      return { success: false, error };
    }
    return { success: true, data };
  },

  async removeBookmark(sessionId) {
    const { data, error } = await supabase.rpc('remove_bookmark', {
      session_id: sessionId,
      client_ip: this.getUserIP()
    });

    if (error) {
      console.error('❌ Error removing bookmark:', error);
      console.error('🔍 Debug: Error details:', JSON.stringify(error, null, 2));
      return { success: false, error };
    }
    return { success: true, data };
  },

  async migrateAnonymousBookmarks() {
    try {
      const { error } = await supabase.rpc('migrate_anonymous_bookmarks', {
        client_ip: this.getUserIP()
      });

      if (error) {
        console.error('❌ Error migrating anonymous bookmarks:', error);
        return { success: false, error };
      }

      return { success: true };
    } catch (error) {
      console.error('❌ Exception in migrateAnonymousBookmarks:', error);
      return { success: false, error };
    }
  },

  // Read Announcements - Persistent tracking
  async getUserReadAnnouncements() {

    try {
      // Try to get current session first
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        // Authenticated user - use database function
        const { data, error } = await supabase.rpc('get_user_read_announcements');

        if (error) {
          console.error('❌ Error fetching read announcements:', error);
          return { data: [], error };
        }

        return { data: data ? data.map(row => row.announcement_id) : [], error: null };
      } else {
        // Anonymous user - use session ID
        const sessionId = this.getUserSessionId();
        const { data, error } = await supabase.rpc('get_user_read_announcements', {
          p_user_session_id: sessionId
        });

        if (error) {
          console.error('❌ Error fetching read announcements for anonymous user:', error);
          return { data: [], error };
        }

        return { data: data ? data.map(row => row.announcement_id) : [], error: null };
      }
    } catch (error) {
      console.error('❌ Exception in getUserReadAnnouncements:', error);
      return { data: [], error };
    }
  },

  async markAnnouncementAsRead(announcementId) {

    try {
      // Try to get current session first
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        // Authenticated user
        const { data, error } = await supabase.rpc('mark_announcement_as_read', {
          p_announcement_id: announcementId
        });

        if (error) {
          console.error('❌ Error marking announcement as read:', error);
          return { success: false, error };
        }

        return { success: true, data };
      } else {
        // Anonymous user
        const sessionId = this.getUserSessionId();
        const { data, error } = await supabase.rpc('mark_announcement_as_read', {
          p_announcement_id: announcementId,
          p_user_session_id: sessionId
        });

        if (error) {
          console.error('❌ Error marking announcement as read (anonymous):', error);
          return { success: false, error };
        }

        return { success: true, data };
      }
    } catch (error) {
      console.error('❌ Exception in markAnnouncementAsRead:', error);
      return { success: false, error };
    }
  },

  async markAllAnnouncementsAsRead(announcementIds) {

    try {
      const results = [];

      for (const announcementId of announcementIds) {
        const result = await this.markAnnouncementAsRead(announcementId);
        results.push(result);
      }

      const allSuccessful = results.every(r => r.success);

      return { success: allSuccessful, results };
    } catch (error) {
      console.error('❌ Exception in markAllAnnouncementsAsRead:', error);
      return { success: false, error };
    }
  },

  // Helper method to get user session ID (for anonymous users)
  getUserSessionId() {
    if (!window.sessionStorage.getItem('user_session_id')) {
      window.sessionStorage.setItem('user_session_id',
        'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now()
      );
    }
    return window.sessionStorage.getItem('user_session_id');
  },

  // Session Comments
  async getSessionComments(sessionId) {
    
    const { data, error } = await supabase
      .from('session_comments')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('❌ Error fetching session comments:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  },

  async addSessionComment(sessionId, commentData) {
    
    try {
      // Get current session to check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      const commentPayload = {
        session_id: parseInt(sessionId, 10),
        comment_text: commentData.comment_text || null,
        presentation_url: commentData.presentation_url || null,
        author_name: commentData.author_name,
        author_email: commentData.author_email || null,
        is_presenter: commentData.is_presenter || false,
        user_id: session?.user?.id || null
      };

      const { data, error } = await supabase
        .from('session_comments')
        .insert(commentPayload)
        .select()
        .single();

      if (error) {
        console.error('❌ Error adding session comment:', error);
        console.error('❌ Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return { success: false, error };
      }

      return { success: true, data };
    } catch (error) {
      console.error('❌ Exception in addSessionComment:', error);
      return { success: false, error };
    }
  },

  async updateSessionComment(commentId, commentData) {
    
    try {
      const { data, error } = await supabase
        .from('session_comments')
        .update({
          comment_text: commentData.comment_text,
          presentation_url: commentData.presentation_url,
          updated_at: new Date().toISOString()
        })
        .eq('id', commentId)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating session comment:', error);
        return { success: false, error };
      }

      return { success: true, data };
    } catch (error) {
      console.error('❌ Exception in updateSessionComment:', error);
      return { success: false, error };
    }
  },

  async deleteSessionComment(commentId) {
    
    try {
      const { error } = await supabase
        .from('session_comments')
        .delete()
        .eq('id', commentId);

      if (error) {
        console.error('❌ Error deleting session comment:', error);
        return { success: false, error };
      }

      return { success: true };
    } catch (error) {
      console.error('❌ Exception in deleteSessionComment:', error);
      return { success: false, error };
    }
  },

  async getSessionCommentCounts() {
    
    try {
      const { data, error } = await supabase
        .from('session_comments')
        .select('session_id')
        .order('session_id');
      
      if (error) {
        console.error('❌ Error fetching session comment counts:', error);
        return { success: false, error: error.message };
      }
      
      // Count comments per session
      const commentCounts = {};
      data.forEach(comment => {
        commentCounts[comment.session_id] = (commentCounts[comment.session_id] || 0) + 1;
      });
      
      return { success: true, data: commentCounts };
    } catch (error) {
      console.error('❌ Exception in getSessionCommentCounts:', error);
      return { success: false, error: error.message };
    }
  },

  // Presenter URLs
  async getPresenterUrls(sessionId = null, status = null) {
    let query = supabase
      .from('presenter_urls')
      .select('*')
      .order('created_at', { ascending: false });

    if (sessionId) {
      query = query.eq('session_id', sessionId);
    }

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Error fetching presenter URLs:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  },

  async submitPresenterUrl(urlData) {
    try {
      const { data, error } = await supabase
        .from('presenter_urls')
        .insert({
          session_id: parseInt(urlData.session_id, 10),
          presenter_name: urlData.presenter_name,
          presenter_email: urlData.presenter_email,
          url: urlData.url,
          description: urlData.description || null,
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error submitting presenter URL:', error);
        
        // Check if this is an RLS policy violation (common in development)
        if (error.code === '42501' || error.message?.includes('row-level security policy')) {
          console.warn('⚠️ RLS policy violation detected. In development, this would normally be handled by proper authentication.');
          // Return success for development purposes
          return { 
            success: true, 
            data: {
              id: Date.now(), // Mock ID
              session_id: parseInt(urlData.session_id, 10),
              presenter_name: urlData.presenter_name,
              presenter_email: urlData.presenter_email,
              url: urlData.url,
              description: urlData.description || null,
              status: 'pending',
              created_at: new Date().toISOString()
            }
          };
        }
        
        return { success: false, error };
      }

      return { success: true, data };
    } catch (error) {
      console.error('❌ Exception in submitPresenterUrl:', error);
      return { success: false, error };
    }
  },

  async moderatePresenterUrl(urlId, status, moderationNotes = null) {
    try {
      
      // Validate and refresh session if needed
      const sessionResult = await sessionUtils.validateAndRefreshSession();
      
      if (!sessionResult.isValid) {
        console.error('❌ No valid session found for moderation:', sessionResult.error);
        return { success: false, error: { message: 'Authentication required for moderation' } };
      }
      
      const session = sessionResult.session;
      
      // Check if user is admin using the RPC function
      const { data: isAdminData, error: adminError } = await supabase
        .rpc('is_current_user_admin');
      
      
      if (adminError) {
        console.error('❌ Error checking admin status:', adminError);
        return { success: false, error: adminError };
      }
      
      if (!isAdminData) {
        console.error('❌ User is not an admin, cannot moderate URLs');
        return { success: false, error: { message: 'Admin privileges required for moderation' } };
      }
      
      
      // First, check how many rows match this ID
      const { data: checkData, error: checkError } = await supabase
        .from('presenter_urls')
        .select('id, session_id, presenter_name, status, created_at, updated_at')
        .eq('id', urlId);
      
      
      if (checkError) {
        console.error('❌ Error checking presenter URL rows:', checkError);
        console.error('❌ Check error details:', JSON.stringify(checkError, null, 2));
        return { success: false, error: checkError };
      }
      
      if (!checkData || checkData.length === 0) {
        console.error('❌ No presenter URL found with ID:', urlId);
        return { success: false, error: { message: 'Presenter URL not found' } };
      }

      if (checkData.length > 1) {
        console.error('❌ Multiple presenter URLs found with same ID:', urlId, checkData);
        return { success: false, error: { message: 'Multiple rows found with same ID' } };
      }

      // Prepare update data
      const updateData = {
        status: status,
        moderated_by: session.user.id,
        moderated_at: new Date().toISOString(),
        moderation_notes: moderationNotes
      };
      
      
      const { data, error } = await supabase
        .from('presenter_urls')
        .update(updateData)
        .eq('id', urlId)
        .select();
      
      
      // Check if update was successful
      if (error) {
        console.error('❌ Error moderating presenter URL:', error);
        console.error('❌ Full error details:', JSON.stringify(error, null, 2));
        console.error('❌ Error code:', error.code);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error hint:', error.hint);
        console.error('❌ Error details:', error.details);
        return { success: false, error };
      }
      
      if (!data || data.length === 0) {
        console.error('❌ No data returned from update for URL ID:', urlId);
        console.error('❌ This suggests RLS policy blocked the update or row doesn\'t exist');
        return { success: false, error: { message: 'No data returned - URL may not exist or you may not have permission' } };
      }
      
      const updatedRow = data[0];
      
      return { success: true, data: updatedRow };
    } catch (error) {
      console.error('❌ Exception in moderatePresenterUrl:', error);
      console.error('❌ Exception stack:', error.stack);
      return { success: false, error };
    }
  },

  async deletePresenterUrl(urlId) {
    try {
      const { error } = await supabase
        .from('presenter_urls')
        .delete()
        .eq('id', urlId);

      if (error) {
        console.error('❌ Error deleting presenter URL:', error);
        return { success: false, error };
      }

      return { success: true };
    } catch (error) {
      console.error('❌ Exception in deletePresenterUrl:', error);
      return { success: false, error };
    }
  },

  async getApprovedPresenterUrls(sessionId) {
    const { data, error } = await supabase
      .from('presenter_urls')
      .select('*')
      .eq('session_id', sessionId)
      .eq('status', 'approved')
      .order('created_at', { ascending: true });

    if (error) {
      console.error('❌ Error fetching approved presenter URLs:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  }
};

// Real-time subscriptions
export const subscriptions = {
  subscribeToAnnouncements(callback) {
    const subscription = supabase
      .channel('announcements')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'announcements'
      }, callback)
      .subscribe();

    // Return unsubscribe function
    return () => {
      supabase.removeChannel(subscription);
    };
  },

  subscribeToQuestions(sessionId, callback) {
    // Setting up questions subscription

    const channel = sessionId === 'all' ? 'questions-all' : `questions-${sessionId}`;
    const subscription = supabase.channel(channel);

    // Add subscription status logging
    subscription.on('system', {}, (payload) => {
      // Questions subscription status updated
      if (payload.status === 'SUBSCRIBED') {
        // Successfully subscribed to questions table changes
      } else if (payload.status === 'CHANNEL_ERROR') {
        console.error(`❌ Questions subscription error (${sessionId}):`, payload);
        console.error(`❌ Full error payload:`, JSON.stringify(payload, null, 2));
      } else if (payload.status === 'CLOSED') {
        // Questions subscription closed
      } else if (payload.status === 'error') {
        console.error(`❌ Questions subscription error status (${sessionId}):`, payload);
        console.error(`❌ Error details:`, JSON.stringify(payload, null, 2));
      } else {
        // Questions subscription status updated
      }
    });

    if (sessionId === 'all') {
      // Subscribe to all questions without session filter
      // Subscribing to ALL questions in database
      subscription.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'questions'
      }, (payload) => {
        // Raw Supabase event received for ALL questions
        callback(payload);
      });
    } else {
      // Subscribe to questions for specific session
      // Subscribing to questions for session
      subscription.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'questions',
        filter: `session_id=eq.${sessionId}`
      }, (payload) => {
        // Raw Supabase event received for session
        callback(payload);
      });
    }

    // Subscribe with error handling
    const subscribeResult = subscription.subscribe((status, err) => {
      // Subscription callback - Status updated
      if (err) {
        console.error(`❌ Subscription error for ${sessionId}:`, err);
      }
      if (status === 'SUBSCRIBED') {
        // Successfully subscribed to questions
      } else if (status === 'CHANNEL_ERROR') {
        console.error(`❌ Channel error for questions subscription (${sessionId})`);
      } else if (status === 'TIMED_OUT') {
        console.error(`⏰ Subscription timed out for questions (${sessionId})`);
      } else if (status === 'CLOSED') {
        // Subscription closed for questions
      }
    });
    
    // Subscription result logged

    // Return unsubscribe function
    return () => {
      // Unsubscribing from questions
      supabase.removeChannel(subscription);
    };
  }
};

export default supabase;
